'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';

/**
 * Vuukle Widgets — comments, mounted directly into the page via Vuukle's
 * own hardcoded `#vuukle-comments` div (no iframe wrapper).
 *
 * Extracted verbatim from src/app/api/articles/ArticleListInfinite.tsx so both
 * the legacy per-story-template renderer and the dynamic article-template
 * renderer (ArticleComments component type) mount the identical widget.
 */
export interface VuukleCommentsProps {
  articleId: string;
  articleTitle: string;
  articleUrl: string;
  articleImage: string;
  articleTags: string;
  articleAuthor: string;
  /** Builder-authored note shown above the comment box, e.g. a moderation/
   *  views notice ("The views expressed in the comments below are those of
   *  the users..."). Unset/empty renders nothing — matches every other
   *  Builder-authored text field's "hide, don't show placeholder" behavior. */
  disclaimer?: string;
}

/** How far ahead of the viewport the comments widget starts loading. */
const LAZY_ROOT_MARGIN = '600px 0px';
const VUUKLE_SCRIPT_ID = 'vuukle-platform-script';
export default function VuukleComments(t0) {
  const $ = _c(17);
  const {
    articleId,
    articleTitle,
    articleUrl,
    articleImage,
    articleTags,
    articleAuthor,
    disclaimer
  } = t0;
  const containerRef = useRef(null);
  const [active, setActive] = useState(false);
  let t1;
  let t2;
  if ($[0] !== active) {
    t1 = () => {
      if (active) {
        return;
      }
      const el = containerRef.current;
      if (!el) {
        return;
      }
      if (typeof IntersectionObserver === "undefined") {
        setActive(true);
        return;
      }
      const io = new IntersectionObserver(entries => {
        if (entries.some(_temp)) {
          setActive(true);
          io.disconnect();
        }
      }, {
        rootMargin: LAZY_ROOT_MARGIN
      });
      io.observe(el);
      return () => io.disconnect();
    };
    t2 = [active];
    $[0] = active;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== active || $[4] !== articleAuthor || $[5] !== articleId || $[6] !== articleImage || $[7] !== articleTags || $[8] !== articleTitle || $[9] !== articleUrl) {
    t3 = () => {
      if (!active) {
        return;
      }
      (window as any).VUUKLE_CONFIG = {
        apiKey: "6f27340a-c671-45a9-ae0c-13b8d5595b42",
        host: "dinamani.com",
        articleId,
        img: articleImage,
        tags: articleTags,
        url: articleUrl,
        title: articleTitle,
        author: articleAuthor,
        language: "en",
        comments: {
          transliteration: {
            language: "ta",
            enabledByDefault: false
          }
        }
      };
      if (document.getElementById(VUUKLE_SCRIPT_ID)) {
        return;
      }
      const script = document.createElement("script");
      script.id = VUUKLE_SCRIPT_ID;
      script.src = "https://cdn.vuukle.com/platform.js";
      script.async = true;
      document.body.appendChild(script);
    };
    t4 = [active, articleId, articleImage, articleTags, articleUrl, articleTitle, articleAuthor];
    $[3] = active;
    $[4] = articleAuthor;
    $[5] = articleId;
    $[6] = articleImage;
    $[7] = articleTags;
    $[8] = articleTitle;
    $[9] = articleUrl;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== disclaimer) {
    t5 = disclaimer && <p className="text-12-tamil-400 text-808080 px-3 pt-3 pb-2">{disclaimer}</p>;
    $[12] = disclaimer;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="px-3" ref={containerRef}><div id="vuukle-comments" /></div>;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5) {
    t7 = <>{t5}{t6}</>;
    $[15] = t5;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function _temp(e) {
  return e.isIntersecting;
}